/**
 * idbSyncMiddleware — write-through Redux → IndexedDB sync layer.
 *
 * Intercepts relevant action types AFTER the reducer has run, then mirrors
 * the updated state to IndexedDB asynchronously (fire-and-forget). All IDB
 * writes are non-blocking: failures are silently logged and never propagate
 * to the UI.
 *
 * Actions marked with fromCache:true (hydration dispatches) are skipped so
 * we never redundantly write IDB data back to IDB.
 */

import {
    RECENT_CHAT_DATA,
    ACTIVE_CHAT_UPDATE,
    RECENT_NOTIFICATION_UPDATE,
    RECENT_RECALL_UPDATE,
    RECENT_REMOVE_MESSAGE_UPDATE,
    UPDATE_MUTE_RECENT_CHAT,
    UPDATE_ARCHIVE_RECENT_CHAT,
    UPDATE_PIN_RECENT_CHAT,
    UPDATE_FAVOURITE_RECENT_CHAT,
    UPDATE_UNREAD_FLAG_RECENT_CHAT,
    UPDATE_MESSAGE_STATUS,
    UPDATE_MESSAGE_REACTION,
    RECONNECT_RECENT_CHAT_UPDATE,
    DELETE_SINGLE_CHAT,
    RESET_RECENT_CHAT,
    CLEAR_ALL_CHAT,
    CHAT_MESSAGE_HISTORY,
    DELETE_CHAT_HISTORY,
    CLEAR_CHAT_HISTORY,
    CLEAR_CHAT_HISTORY_ACTION_COMMON,
    GROUPS_DATA,
    GROUPS_UPDATE_DATA,
    RECONNECT_GROUPS_UPDATE_DATA,
    UNREAD_USER_OBJ,
    UNREAD_MESSAGE_DELETE,
    UNREAD_MESSAGE_UPDATE,
    DECREASE_UNREAD_COUNT,
    DISAPPEAR_MESSAGE,
    DELETE_MESSAGE_FOR_ME,
    VCARD_DATA,
} from '../Actions/Constants';
import IndexedDb from './IndexedDb';
import logger from './logger';
import messagesIdb from './MessagesIdb';
import recentChatIdb from './RecentChatIdb';
import { scheduleMessageExpiry } from './Chat/DisappearScheduler';

const idb = new IndexedDb();

/**
 * Resolve the plain chatId (no @domain) from the filterBy field used by
 * recent-chat update actions.
 */
const normalizeChatId = id => (id && id.includes('@') ? id.split('@')[0] : id);

/**
 * Copy a message's new reactions onto its cached copy in chatsdb (FR93).
 * A reload restores the thread from that cache without refetching, so it must not go stale.
 */
const persistMessageReactions = (store, chatId, msgId) => {
    const message = store.getState().chatConversationHistory?.data?.[chatId]?.messages?.[msgId];
    if (!message) return;
    messagesIdb
        .getMessageById(msgId)
        .then(cached => cached && messagesIdb.updateMessage({ ...cached, reactions: message.reactions || [] }))
        .catch(error => logger.warn('[IDB] reactions not cached', { chatId, msgId, error }));
};

export const idbSyncMiddleware = store => next => action => {
    const result = next(action); // Let the reducer run first — state is now updated

    // Schedule disappearing-message timeouts on every CHAT_MESSAGE_HISTORY load,
    // including hydration (fromCache:true) dispatches — must run before the early return.
    if (action.type === CHAT_MESSAGE_HISTORY) {
        const rawData = action.payload?.data;
        const chatId = rawData?.userJid?.split('@')[0] || rawData?.groupJid?.split('@')[0];
        if (chatId) {
            const messages = store.getState().chatConversationHistory?.data?.[chatId]?.messages || {};
            Object.values(messages).forEach(msg => {
                if (msg.disappear_at || msg.disappearAt) scheduleMessageExpiry(msg, chatId);
            });
        }
    }

    // Skip any hydration dispatches to avoid writing IDB data back to IDB
    if (action.payload?.fromCache) return result;

    switch (action.type) {
        // ── Recent Chats ──────────────────────────────────────────────────────

        case RECENT_CHAT_DATA: {
            const data = action.payload?.data;
            if (Array.isArray(data) && data.length) {
                idb.saveRecentChats(data);
                idb.setMetadata('recentChats', { lastSyncAt: Date.now() });
                // Schedule expiry for last messages with disappear_at (covers chats not yet opened)
                data.forEach(recentChat => {
                    const disappearAt = recentChat.disappear_at || recentChat.disappearAt;
                    if (disappearAt && recentChat.msgId && recentChat.fromUserId) {
                        scheduleMessageExpiry({ ...recentChat, disappear_at: disappearAt }, recentChat.fromUserId);
                    }
                });
            }
            break;
        }

        // Single-chat mutations: read the updated item from the post-reducer state
        // and persist to BOTH IDB stores so the prefetch cache (recentChatIdb / chatsdb)
        // always reflects the latest Redux state — including disappear-expiry clears.
        case ACTIVE_CHAT_UPDATE:
        case RECENT_NOTIFICATION_UPDATE: {
            const filterBy = action.payload?.filterBy;
            if (filterBy) {
                const chatId = normalizeChatId(filterBy);
                const updated = store
                    .getState()
                    .recentChatData?.data?.find(c => normalizeChatId(c.fromUserId) === chatId);
                if (updated) {
                    idb.upsertRecentChat(updated);
                    recentChatIdb.updateRecentChat(chatId, { ...updated });
                }
            }
            break;
        }

        case RECENT_RECALL_UPDATE:
        case RECENT_REMOVE_MESSAGE_UPDATE: {
            const data = action.payload?.data;
            const chatId = normalizeChatId(data?.fromUserId || data?.filterBy);
            if (chatId) {
                const updated = store
                    .getState()
                    .recentChatData?.data?.find(c => normalizeChatId(c.fromUserId) === chatId);
                if (updated) idb.upsertRecentChat(updated);
            }
            break;
        }

        case UPDATE_MUTE_RECENT_CHAT:
        case UPDATE_ARCHIVE_RECENT_CHAT:
        case UPDATE_PIN_RECENT_CHAT:
        case UPDATE_FAVOURITE_RECENT_CHAT:
        case UPDATE_UNREAD_FLAG_RECENT_CHAT: {
            const chatId = normalizeChatId(action.payload?.data?.fromUserId);
            if (chatId) {
                const updated = store
                    .getState()
                    .recentChatData?.data?.find(c => normalizeChatId(c.fromUserId) === chatId);
                // A reload lists chats from recentChatIdb, so the flag must land there too.
                if (updated) {
                    idb.upsertRecentChat(updated);
                    recentChatIdb.updateRecentChat(chatId, { ...updated });
                }
            }
            break;
        }

        case UPDATE_MESSAGE_STATUS:
        case UPDATE_MESSAGE_REACTION: {
            const chatId = normalizeChatId(action.payload?.data?.fromUserId);
            if (chatId) {
                const updated = store
                    .getState()
                    .recentChatData?.data?.find(c => normalizeChatId(c.fromUserId) === chatId);
                if (updated) idb.upsertRecentChat(updated);
            }
            if (chatId && action.type === UPDATE_MESSAGE_REACTION) {
                persistMessageReactions(store, chatId, action.payload?.data?.msgId);
            }
            break;
        }

        // After a reconnect merge, persist the full updated recent-chat list
        case RECONNECT_RECENT_CHAT_UPDATE: {
            const recentData = store.getState().recentChatData?.data;
            if (Array.isArray(recentData) && recentData.length) {
                idb.saveRecentChats(recentData);
                idb.setMetadata('recentChats', { lastSyncAt: Date.now() });
            }
            break;
        }

        case DELETE_SINGLE_CHAT: {
            const chatId = normalizeChatId(action.payload?.data?.fromUserId);
            if (chatId) {
                idb.removeRecentChat(chatId);
                idb.deleteMessagesByChatId(chatId);
            }
            break;
        }

        // Logout: wipe all cached chat data
        case RESET_RECENT_CHAT: {
            idb.clearChatData();
            break;
        }

        // Clear all chat content but preserve conversation structure
        case CLEAR_ALL_CHAT: {
            // Re-persist the cleared recent chats (msgBody wiped by reducer)
            const recentData = store.getState().recentChatData?.data;
            if (Array.isArray(recentData)) idb.saveRecentChats(recentData);
            break;
        }

        // ── Chat Messages ─────────────────────────────────────────────────────

        case CHAT_MESSAGE_HISTORY: {
            const rawData = action.payload?.data;
            // Extract chatId from the SDK response shape
            const chatId = rawData?.userJid?.split('@')[0] || rawData?.groupJid?.split('@')[0];
            if (chatId) {
                const chatHistory = store.getState().chatConversationHistory?.data?.[chatId];
                if (chatHistory?.messages && Object.keys(chatHistory.messages).length) {
                    idb.saveMessages(chatId, Object.values(chatHistory.messages));
                    idb.setMetadata(`messages_${chatId}`, { lastSyncAt: Date.now() });
                }
            }
            break;
        }

        case DELETE_CHAT_HISTORY: {
            const chatId = normalizeChatId(action.payload?.data?.fromUserId);
            if (chatId) {
                idb.deleteMessagesByChatId(chatId);
                idb.removeRecentChat(chatId);
            }
            break;
        }

        case CLEAR_CHAT_HISTORY:
        case CLEAR_CHAT_HISTORY_ACTION_COMMON: {
            const chatId = normalizeChatId(action.payload?.data?.fromUserId || action.payload?.data?.id);
            if (chatId) idb.deleteMessagesByChatId(chatId);
            break;
        }

        // A single message was removed by the disappear timer — delete it from BOTH IDB stores.
        // mirrorflydb: idb.deleteMessage removes from localforage chatMessages.
        // chatsdb: messagesIdb.updateDeletedMessagesInIdb removes from the native IDB messages
        //   store (keyed by msgId). Without this, the prefetch sync re-adds expired messages
        //   from chatsdb on the next page refresh (observed as messages "coming back").
        case DISAPPEAR_MESSAGE: {
            const { chatId, msgId } = action.payload?.data || {};
            const normalizedId = normalizeChatId(chatId);
            if (normalizedId && msgId) {
                idb.deleteMessage(normalizedId, msgId);
                messagesIdb.updateDeletedMessagesInIdb(DELETE_MESSAGE_FOR_ME, { msgIds: [msgId] });
            }
            break;
        }

        // ── Own account msgDisappear ─────────────────────────────────────────
        // Persist the account-level disappear setting to localforage so it can be
        // restored by hydrateFromIDB on the next page load, BEFORE getUserProfile
        // fires and potentially overwrites vCardData without the field.
        case VCARD_DATA: {
            const msgDisappear = action.payload?.data?.msgDisappear;
            if (msgDisappear !== undefined && msgDisappear !== null) {
                idb.setMetadata('own_msgDisappear', { value: Number(msgDisappear) });
            }
            break;
        }

        // ── Groups ────────────────────────────────────────────────────────────

        case GROUPS_DATA: {
            const data = action.payload?.data;
            if (Array.isArray(data) && data.length) {
                idb.saveGroups(data);
                idb.setMetadata('groupsList', { lastSyncAt: Date.now() });
            }
            break;
        }

        case GROUPS_UPDATE_DATA:
        case RECONNECT_GROUPS_UPDATE_DATA: {
            // A single group was updated/added; persist the full updated list
            const groupsData = store.getState().groupsData?.data;
            if (Array.isArray(groupsData) && groupsData.length) {
                idb.saveGroups(groupsData);
            }
            break;
        }

        // ── Unread Count Sync ─────────────────────────────────────────────────
        // Keep IDB recentChats.unreadCount in sync so the next hydration
        // dispatches the correct badge count instead of the stale API value.

        case UNREAD_USER_OBJ: {
            const { fromUserId, count } = action.payload?.data || {};
            if (fromUserId !== undefined) {
                const chatId = normalizeChatId(fromUserId);
                // Merge only the unread fields; preserves all other chat fields
                idb.upsertRecentChat({
                    fromUserId: chatId,
                    unreadCount: count || 0,
                    unreadMessageId: action.payload?.data?.fetchLastMsgId || '',
                });
            }
            break;
        }

        // Keep IDB unreadCount in sync with every Redux unread update so that
        // hydrateFromIDB restores the correct badge on the next page load.
        case UNREAD_MESSAGE_UPDATE: {
            const fromUserId = action.payload?.data?.fromUserId;
            if (fromUserId) {
                const chatId = normalizeChatId(fromUserId);
                const updatedCount = store.getState().unreadCountData?.unreadDataObj?.[chatId]?.count || 0;
                idb.upsertRecentChat({ fromUserId: chatId, unreadCount: updatedCount });
            }
            break;
        }

        case UNREAD_MESSAGE_DELETE: {
            // User deleted/cleared unread state for a chat → zero it in IDB
            const { fromUserId } = action.payload?.data || {};
            if (fromUserId) {
                const chatId = normalizeChatId(fromUserId);
                idb.upsertRecentChat({ fromUserId: chatId, unreadCount: 0, unreadMessageId: '' });
            }
            break;
        }

        case DECREASE_UNREAD_COUNT: {
            // Fired by dispatchDisappearMessage when an unread message expires.
            // Persist the post-decrement count to BOTH IDB stores so the next hydration
            // reads the correct value instead of restoring the stale count.
            //
            // Both stores require the FULL entry (not a partial { unreadCount: N }):
            //   • recentChatIdb uses objectStore.put — a full replace, so partial writes
            //     wipe all other fields (banner, unreadMessageId, etc.)
            //   • idb.upsertRecentChat does a read-merge-write; if called concurrently
            //     with ACTIVE_CHAT_UPDATE it may read stale data and lose banner fields.
            // Using the complete Redux entry avoids both problems.
            const { fromUserId } = action.payload?.data || {};
            if (fromUserId) {
                const chatId = normalizeChatId(fromUserId);
                // next(action) ran first — unreadCountData already reflects the decrement.
                // recentChatData already has the banner (ACTIVE_CHAT_UPDATE fired before
                // DECREASE_UNREAD_COUNT inside dispatchDisappearMessage).
                const updatedCount = store.getState().unreadCountData?.unreadDataObj?.[chatId]?.count ?? 0;
                const fullEntry = store
                    .getState()
                    .recentChatData?.data?.find(c => normalizeChatId(c.fromUserId) === chatId);
                if (fullEntry) {
                    idb.upsertRecentChat({ ...fullEntry, unreadCount: updatedCount });
                    recentChatIdb.updateRecentChat(chatId, { ...fullEntry, unreadCount: updatedCount });
                }
            }
            break;
        }

        default:
            break;
    }

    return result;
};
